'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import HeroSection from '@/components/sections/HeroSection';
import ClientsPartnersSection from '@/components/sections/ClientsPartnersSection';
import BankingFeaturesSection from '@/components/sections/BankingFeaturesSection';
import CurrencyAccountSection from '@/components/sections/CurrencyAccountSection';
import SmartPaymentSection from '@/components/sections/SmartPaymentSection';
import StatsSection from '@/components/sections/StatsSection';
import PricingSection from '@/components/sections/PricingSection';
import SmartBankingSection from '@/components/sections/SmartBankingSection';
import TestimonialsSection from '@/components/sections/TestimonialsSection';
import FinalCTASection from '@/components/sections/FinalCTASection';
import MobileBankingSection from '@/components/sections/MobileBankingSection';
import CustomerSupportSection from '@/components/sections/CustomerSupportSection';
import WealthGrowthSection from '@/components/sections/WealthGrowthSection';

const FintechBankingLandingPage = () => {
  // Currency data for the hero section
  const currencyData = [
    { currency: 'USD', amount: '2,420.00', change: '+2.5%', positive: true },
    { currency: 'EUR', amount: '1,840.00', change: '-1.2%', positive: false },
    { currency: 'GBP', amount: '1,960.00', change: '+0.8%', positive: true },
    { currency: 'JPY', amount: '245,600', change: '+1.4%', positive: true },
  ];

  

  // Banking features data
  const bankingFeatures = [
    {
      icon: '/images/img_frame_2121457469.svg',
      title: 'Instant Transfers',
      description: 'Send money instantly to anyone, anywhere in the world with zero fees and real-time processing.',
    },
    {
      icon: '/images/img_frame_2121457470.svg',
      title: 'Smart Savings',
      description: 'Automated savings that help you reach your financial goals faster with intelligent algorithms.',
    },
    {
      icon: '/images/img_frame_2121457471.svg',
      title: 'Investment Tools',
      description: 'Professional-grade investment tools and insights to grow your wealth with confidence.',
    },
    {
      icon: '/images/img_frame_2121457472.svg',
      title: 'Security First',
      description: 'Bank-level security with biometric authentication and advanced fraud protection.',
    },
    {
      icon: '/images/img_frame_2121457473.svg',
      title: '24/7 Support',
      description: 'Round-the-clock customer support from our team of financial experts.',
    },
    {
      icon: '/images/img_frame_2121457474.svg',
      title: 'Multi-Currency',
      description: 'Hold and exchange multiple currencies with competitive rates and no hidden fees.',
    },
  ];

  // Stats data
  const statsData = [
    { number: '2M+', label: 'Active Users', description: 'Trust our platform' },
    { number: '$50B+', label: 'Transactions', description: 'Processed securely' },
    { number: '99.9%', label: 'Uptime', description: 'Reliable service' },
    { number: '150+', label: 'Countries', description: 'Worldwide coverage' },
  ];

  // Pricing plans data
  const pricingPlans = [
    {
      name: 'Basic',
      price: 'Free',
      period: '',
      description: 'Perfect for getting started with basic banking features',
      features: [
        'Free account opening',
        'Basic money transfers',
        'Mobile app access',
        'Email support',
        '1 currency account',
      ],
      popular: false,
    },
    {
      name: 'Premium',
      price: '$9.99',
      period: '/month',
      description: 'Advanced features for growing your financial portfolio',
      features: [
        'Everything in Basic',
        'Priority transfers',
        'Investment tools',
        'Phone & chat support',
        '5 currency accounts',
        'Advanced analytics',
      ],
      popular: true,
    },
    {
      name: 'Business',
      price: '$29.99',
      period: '/month',
      description: 'Complete solution for businesses and entrepreneurs',
      features: [
        'Everything in Premium',
        'Business accounts',
        'Team management',
        'API access',
        'Unlimited currencies',
        'Dedicated support',
        'Custom integrations',
      ],
      popular: false,
    },
  ];

  // Testimonials data
  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Entrepreneur',
      content: 'This platform has revolutionized how I manage my business finances. The multi-currency support is incredible!',
      rating: 5,
      avatar: '/images/img_ellipse_1.png',
    },
    {
      name: 'Michael Chen',
      role: 'Freelancer',
      content: 'Fast, secure, and reliable. I can focus on my work while the platform handles all my financial needs seamlessly.',
      rating: 5,
      avatar: '/images/img_ellipse_2.png',
    },
    {
      name: 'Emily Rodriguez',
      role: 'Small Business Owner',
      content: 'The investment tools have helped me grow my savings significantly. Highly recommend to anyone serious about their finances.',
      rating: 5,
      avatar: '/images/img_ellipse_3.png',
    },
    {
      name: 'David Kim',
      role: 'Digital Nomad',
      content: 'Perfect for my lifestyle. I can manage money across different countries without any hassle or excessive fees.',
      rating: 5,
      avatar: '/images/img_ellipse_4.png',
    },
    {
      name: 'Lisa Thompson',
      role: 'Investor',
      content: 'The analytics and insights provided are top-notch. It has made my investment decisions much more informed.',
      rating: 5,
      avatar: '/images/img_ellipse_5.png',
    },
    {
      name: 'James Wilson',
      role: 'Tech Executive',
      content: 'Security and user experience are both excellent. This is the future of digital banking.',
      rating: 5,
      avatar: '/images/img_ellipse_6.png',
    },
  ];

  // Helper function to render star ratings
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <img data-component-id="src/app/page.jsx:172:6" data-component-path="src/app/page.jsx" data-component-line="172" data-component-end-line="177" data-component-file="page.jsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22alt%22%3A%22Star%22%2C%22className%22%3A%22w-%5B16px%5D%20h-%5B16px%5D%20sm%3Aw-%5B18px%5D%20sm%3Ah-%5B18px%5D%20md%3Aw-%5B20px%5D%20md%3Ah-%5B20px%5D%22%7D"
        key={index}
        src={index < rating ? '/images/img_star_1.svg' : '/images/img_star_2.svg'}
        alt="Star"
        className="w-[16px] h-[16px] sm:w-[18px] sm:h-[18px] md:w-[20px] md:h-[20px]"
      />
    ));
  };

  return (
    <div data-component-id="src/app/page.jsx:182:4" data-component-path="src/app/page.jsx" data-component-line="182" data-component-end-line="224" data-component-file="page.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20bg-global-17%20overflow-x-hidden%22%7D" className="w-full bg-global-17 overflow-x-hidden">
      {/* Hero Section */}
      <HeroSection data-component-id="src/app/page.jsx:184:6" data-component-path="src/app/page.jsx" data-component-line="184" data-component-end-line="184" data-component-file="page.jsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D" />
      
      {/* Clients & Partners Section */}
      <ClientsPartnersSection data-component-id="src/app/page.jsx:187:6" data-component-path="src/app/page.jsx" data-component-line="187" data-component-end-line="187" data-component-file="page.jsx" data-component-name="ClientsPartnersSection" data-component-content="%7B%22elementName%22%3A%22ClientsPartnersSection%22%7D" />
      
      {/* Mobile Banking Section - iPhone Mockup */}
      <MobileBankingSection data-component-id="src/app/page.jsx:190:6" data-component-path="src/app/page.jsx" data-component-line="190" data-component-end-line="190" data-component-file="page.jsx" data-component-name="MobileBankingSection" data-component-content="%7B%22elementName%22%3A%22MobileBankingSection%22%7D" />
      
      {/* Banking Features Section */}
      <BankingFeaturesSection data-component-id="src/app/page.jsx:193:6" data-component-path="src/app/page.jsx" data-component-line="193" data-component-end-line="193" data-component-file="page.jsx" data-component-name="BankingFeaturesSection" data-component-content="%7B%22elementName%22%3A%22BankingFeaturesSection%22%7D" bankingFeatures={bankingFeatures} />
      
      {/* Currency Account Section */}
      <CurrencyAccountSection data-component-id="src/app/page.jsx:196:6" data-component-path="src/app/page.jsx" data-component-line="196" data-component-end-line="196" data-component-file="page.jsx" data-component-name="CurrencyAccountSection" data-component-content="%7B%22elementName%22%3A%22CurrencyAccountSection%22%7D" />
      
      {/* Smart Payment Section */}
      <SmartPaymentSection data-component-id="src/app/page.jsx:199:6" data-component-path="src/app/page.jsx" data-component-line="199" data-component-end-line="199" data-component-file="page.jsx" data-component-name="SmartPaymentSection" data-component-content="%7B%22elementName%22%3A%22SmartPaymentSection%22%7D" />
      
      {/* Stats Section */}
      <StatsSection data-component-id="src/app/page.jsx:202:6" data-component-path="src/app/page.jsx" data-component-line="202" data-component-end-line="202" data-component-file="page.jsx" data-component-name="StatsSection" data-component-content="%7B%22elementName%22%3A%22StatsSection%22%7D" statsData={statsData} />
      
      {/* Customer Support Section */}
      <CustomerSupportSection data-component-id="src/app/page.jsx:205:6" data-component-path="src/app/page.jsx" data-component-line="205" data-component-end-line="205" data-component-file="page.jsx" data-component-name="CustomerSupportSection" data-component-content="%7B%22elementName%22%3A%22CustomerSupportSection%22%7D" />
      
      {/* Pricing Section */}
      <PricingSection data-component-id="src/app/page.jsx:208:6" data-component-path="src/app/page.jsx" data-component-line="208" data-component-end-line="208" data-component-file="page.jsx" data-component-name="PricingSection" data-component-content="%7B%22elementName%22%3A%22PricingSection%22%7D" pricingPlans={pricingPlans} />
      
      {/* Smart Banking Section */}
      <SmartBankingSection data-component-id="src/app/page.jsx:211:6" data-component-path="src/app/page.jsx" data-component-line="211" data-component-end-line="211" data-component-file="page.jsx" data-component-name="SmartBankingSection" data-component-content="%7B%22elementName%22%3A%22SmartBankingSection%22%7D" renderStars={renderStars} />
      
      {/* Testimonials Section */}
      <TestimonialsSection data-component-id="src/app/page.jsx:214:6" data-component-path="src/app/page.jsx" data-component-line="214" data-component-end-line="214" data-component-file="page.jsx" data-component-name="TestimonialsSection" data-component-content="%7B%22elementName%22%3A%22TestimonialsSection%22%7D" testimonials={testimonials} renderStars={renderStars} />
      
      {/* Wealth Growth Section */}
      <WealthGrowthSection data-component-id="src/app/page.jsx:217:6" data-component-path="src/app/page.jsx" data-component-line="217" data-component-end-line="217" data-component-file="page.jsx" data-component-name="WealthGrowthSection" data-component-content="%7B%22elementName%22%3A%22WealthGrowthSection%22%7D" />
      
      {/* Final CTA Section */}
      <FinalCTASection data-component-id="src/app/page.jsx:220:6" data-component-path="src/app/page.jsx" data-component-line="220" data-component-end-line="220" data-component-file="page.jsx" data-component-name="FinalCTASection" data-component-content="%7B%22elementName%22%3A%22FinalCTASection%22%7D" renderStars={renderStars} />

      {/* Footer */}
      <Footer data-component-id="src/app/page.jsx:223:6" data-component-path="src/app/page.jsx" data-component-line="223" data-component-end-line="223" data-component-file="page.jsx" data-component-name="Footer" data-component-content="%7B%22elementName%22%3A%22Footer%22%7D" />
    </div>
  );
};

export default FintechBankingLandingPage;
